import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui";
import { IEdoQuestionChoice } from "@/shared/model/edo-question-choice.model";
import { IEdoQuestion } from "@/shared/model/edo-question.model";
import { EdoQuestionTypeEnum } from "@/shared/model/enumerations/edo-question-type.mode";
import { getQuestionType } from "@/shared/util/question-utils";
import { cn } from "@/shared/util/cn";
import { MoreVertical, Pencil, Trash2 } from "lucide-react";
import axios from "axios";
import { useEffect, useState } from "react";
import { Translate, translate } from "react-jhipster";

interface ICourseQuestionProps {
  question: IEdoQuestion;
  className?: string;
  onEdit?: (question: IEdoQuestion) => void;
  onDelete?: (question: IEdoQuestion) => void;
}

const textareaClasses = cn(
  "flex min-h-[80px] w-full rounded-2xl border border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)] px-3 py-2 text-sm text-[var(--color-mockup-text-mid)] dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]/50 dark:text-[var(--color-mockup-text-light)]",
  "cursor-not-allowed resize-none"
);

const CourseQuestion = (props: ICourseQuestionProps) => {
  const [form, setForm] = useState<{ description: string }>({ description: "" });
  const [choices, setChoices] = useState<IEdoQuestionChoice[]>([]);
  const { question, className, onEdit: handleOnEdit, onDelete: handleOnDelete } = props;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({
      ...form,
      [event.target.name]: event.target.value,
    });
  };

  const handleGetChoices = async () => {
    const response = await axios.get<{ data: IEdoQuestionChoice[] }>(
      `/questions/${question.id}/choices`
    );
    setChoices(response.data.data);
  };

  useEffect(() => {
    if (
      question.type === EdoQuestionTypeEnum.SINGLE_CHOICE ||
      question.type === EdoQuestionTypeEnum.TRUE_FALSE ||
      question.type === EdoQuestionTypeEnum.MULTIPLE_CHOICE ||
      question.type === EdoQuestionTypeEnum.FILL_BLANK
    ) {
      handleGetChoices();
    }
  }, [question]);

  const isSingle =
    question.type === EdoQuestionTypeEnum.SINGLE_CHOICE ||
    question.type === EdoQuestionTypeEnum.TRUE_FALSE;

  const getMultipleChoiceOptions = () => {
    if (!choices?.length) {
      return (
        <p className="py-4 text-center text-body text-[var(--color-mockup-text-mid)]">
          <Translate contentKey="edocendoApp.edoQuestionChoice.home.notFound">
            No Edo Question Choices found
          </Translate>
        </p>
      );
    }
    return (
      <fieldset className="mt-4" disabled>
        <legend className="mb-2 text-body font-medium text-[var(--color-mockup-text)]">
          Risposte disponibili
        </legend>
        <div className="space-y-2">
          {choices.map((choice) => (
            <label
              key={choice.id}
              className="flex cursor-default items-start gap-3 rounded-2xl border border-[var(--color-mockup-border)] px-3 py-2 dark:border-[var(--color-mockup-border)]"
            >
              <input
                type={isSingle ? "radio" : "checkbox"}
                name="choices-readonly"
                checked={choice.isCorrect}
                readOnly
                className="mt-1 h-4 w-4 shrink-0 border-[var(--color-mockup-border)] text-primary dark:border-[var(--color-mockup-border)]"
              />
              <span className="text-body text-[var(--color-mockup-text)]">
                {choice.description}
              </span>
            </label>
          ))}
        </div>
      </fieldset>
    );
  };

  return (
    <div
      className={cn(
        "surface overflow-hidden rounded-3xl p-6 transition-all duration-200",
        className
      )}
    >
      <div className="mb-4 flex flex-row items-start justify-between gap-4">
        <div>
          <p className="text-body text-[var(--color-mockup-text)]">
            {question.description}
          </p>
          <p className="mt-1 text-body-sm text-[var(--color-mockup-text-mid)]">
            {getQuestionType(question.type)}
          </p>
        </div>
        {(handleOnEdit || handleOnDelete) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl text-[var(--color-mockup-text-light)] transition-colors hover:bg-[var(--color-surface-secondary)] hover:text-[var(--color-mockup-text-mid)] dark:hover:bg-primary/15 dark:hover:text-[var(--color-mockup-text-light)]"
                aria-label="Altre azioni"
              >
                <MoreVertical className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {handleOnEdit && (
                <DropdownMenuItem onClick={() => handleOnEdit(question)}>
                  <Pencil className="h-4 w-4" />
                  Modifica domanda
                </DropdownMenuItem>
              )}
              {handleOnDelete && (
                <DropdownMenuItem onClick={() => handleOnDelete(question)}>
                  <Trash2 className="h-4 w-4" />
                  Elimina domanda
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className="space-y-4">
        {question.type === EdoQuestionTypeEnum.FREE_TEXT && (
          <div>
            <label className="mb-1 block text-body font-medium text-[var(--color-mockup-text-mid)]">
              {translate("edocendoApp.edoQuestion.description")}
            </label>
            <textarea
              name="description"
              rows={4}
              value={form.description}
              onChange={handleChange}
              disabled
              className={textareaClasses}
            />
          </div>
        )}
        {question.explanationRequired && (
          <div>
            <label className="mb-1 block text-body font-medium text-[var(--color-mockup-text-mid)]">
              {translate("edocendoApp.edoQuestion.description")}
            </label>
            <textarea
              name="description"
              rows={4}
              value={form.description}
              onChange={handleChange}
              disabled
              className={textareaClasses}
            />
          </div>
        )}
        {(question.type === EdoQuestionTypeEnum.SINGLE_CHOICE ||
          question.type === EdoQuestionTypeEnum.TRUE_FALSE ||
          question.type === EdoQuestionTypeEnum.MULTIPLE_CHOICE ||
          question.type === EdoQuestionTypeEnum.FILL_BLANK) &&
          getMultipleChoiceOptions()}
      </div>
    </div>
  );
};

export default CourseQuestion;
